import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, Unlock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { formatDateTime, formatRelativeDate } from "@/lib/formatters";
import {
  listActiveSessions,
  releaseSessionFor,
  type SessionHolder,
} from "@/api/enrichment";

interface ActiveSessionsPanelProps {
  slug: string;
}

type LoadStatus = "loading" | "ok" | "error";

/** Which queue a holder is working. Enrichment and the call center share
 *  the reservation table, so this covers both and tolerates a value the
 *  frontend has not been taught yet.
 *
 *  These are LOCK DOMAINS, not the `queue` values POST /sessions takes:
 *  the request says "work", the holder reads back "enrichment_work".
 *  Verified against a running backend — keying this on the request
 *  vocabulary silently fell through to the raw string. */
const QUEUE_LABELS: Record<string, string> = {
  enrichment_work: "Enrichment",
  enrichment_corrections: "Corrections",
  enrichment_cfo: "CFO research",
  new_prospects: "Call · new prospects",
  follow_ups: "Call · follow-ups",
  no_answer_retry: "Call · no answer",
};

function queueLabel(queue: string): string {
  return QUEUE_LABELS[queue] ?? queue.replace(/_/g, " ");
}

/** Admin view of who is holding rows right now, across enrichment AND the
 *  call center. Its reason to exist is the stuck session: an operator whose
 *  tab died without releasing keeps rows invisible to everyone else until
 *  the three-hour expiry. Releasing by hand gets them back immediately. */
export function ActiveSessionsPanel({ slug }: ActiveSessionsPanelProps) {
  const confirm = useConfirm();
  const [holders, setHolders] = useState<SessionHolder[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [releasingId, setReleasingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      setHolders(await listActiveSessions(slug));
      setStatus("ok");
    } catch (err) {
      setStatus("error");
       
      console.warn("[Enrichment] active sessions load failed", err);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleRelease(holder: SessionHolder) {
    const who = holder.user_name ?? "this operator";
    const ok = await confirm({
      title: "Release reserved rows",
      description: `This frees the ${holder.held} row${holder.held === 1 ? "" : "s"} ${who} is holding, putting them back in the queue for anyone. Do it when a session is stuck, not while someone is working.`,
      confirmLabel: "Release",
      variant: "danger",
      icon: <Unlock className="h-5 w-5" />,
    });
    if (!ok) return;
    setReleasingId(holder.user_id);
    try {
      const res = await releaseSessionFor(slug, holder.user_id);
      toast.success(
        `Released ${res.released} row${res.released === 1 ? "" : "s"}`,
      );
      await load();
    } catch (err) {
      toast.error("Couldn't release the session");
       
      console.warn("[Enrichment] release failed", err);
    } finally {
      setReleasingId(null);
    }
  }

  if (status === "loading" && holders.length === 0) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Active sessions
          </h2>
          <p className="text-sm text-slate-500">
            Rows currently reserved, enrichment and call center alike.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void load()}
          disabled={status === "loading"}
        >
          <RefreshCw
            className={
              status === "loading"
                ? "mr-1.5 h-3.5 w-3.5 animate-spin"
                : "mr-1.5 h-3.5 w-3.5"
            }
          />
          Refresh
        </Button>
      </div>

      {status === "error" ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Couldn&rsquo;t load the active sessions. Try refreshing.
        </div>
      ) : holders.length === 0 ? (
        <div className="rounded-plane bg-white py-16 text-center shadow-plane">
          <p className="text-sm text-slate-500">
            Nobody is holding rows right now.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-plane bg-white shadow-plane">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="px-4 py-3 font-medium">Operator</th>
                <th className="px-4 py-3 font-medium">Queue</th>
                <th className="px-4 py-3 font-medium tabular-nums">Held</th>
                <th className="px-4 py-3 font-medium">Since</th>
                <th className="px-4 py-3 font-medium">Expires</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {holders.map((h) => (
                <tr
                  key={`${h.user_id}:${h.queue}`}
                  className="border-b border-slate-50 last:border-0"
                >
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {h.user_name ?? h.user_id}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {queueLabel(h.queue)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-slate-800">
                    {h.held}
                  </td>
                  <td
                    className="px-4 py-3 text-slate-500"
                    title={formatDateTime(h.oldest_locked_at)}
                  >
                    {formatRelativeDate(h.oldest_locked_at)}
                  </td>
                  <td
                    className="px-4 py-3 text-slate-500"
                    title={formatDateTime(h.expires_at)}
                  >
                    {formatRelativeDate(h.expires_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleRelease(h)}
                      disabled={releasingId === h.user_id}
                    >
                      {releasingId === h.user_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        "Release"
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
